<template>
	<div class="dropdown-list-multiselect">
		<Search
			v-if="options.length > 20"
			v-model="searchQuery"
			:timeout="timeout"
			:placeholder="searchPlaceholder"
		/>
		<ul class="dropdown-list" ref="dropdownlist">
			<li
				v-for="(option, index) in optionsLocal"
				class="dropdown-list__item"
				:key="index"
				:tabindex="0"
				@keydown.up="previousItem"
				@keydown.down="nextItem"
				:hidden="!selectedValuesCopy.includes(option.value)"
			>
				<div class="checkbox">
					<input
						class="checkbox__input"
						:value="option.value"
						v-model="selectedValuesCopy"
						:id="uniqueId + option.text + index + option.value"
						type="checkbox"
						:disabled="option.disabled"
						@change.self="deselectItem"
					/>
					<label
						class="dropdown-list-groups__item"
						v-html="option.text"
						:for="uniqueId + option.text + index + option.value"
						data-test="option"
					/>
				</div>
			</li>
			<li
				v-if="hasSelectedItemsAndLessThanTotalOptions"
				class="dropdown-list__item--div"
			/>
			<li
				v-for="(option, index) in optionsLocal"
				:class="['dropdown-list__item']"
				:key="optionsLocal.length + index + 1"
				:tabindex="0"
				@keydown.up="previousItem"
				@keydown.down="nextItem"
				:hidden="selectedValues.includes(option.value)"
			>
				<div class="checkbox">
					<input
						class="checkbox__input"
						:value="option.value"
						v-model="selectedValues"
						:id="
							uniqueId +
								option.text +
								optionsLocal.length +
								index +
								option.value
						"
						type="checkbox"
						:disabled="option.disabled"
						@change.self="selectItem"
					/>
					<label
						class="dropdown-list-groups__item"
						v-html="option.text"
						:for="
							uniqueId +
								option.text +
								optionsLocal.length +
								index +
								option.value
						"
						data-test="option"
					/>
				</div>
			</li>
		</ul>
	</div>
</template>

<script>
import Search from '@/components/forms/search/search';
import _ from 'lodash';

export default {
	name: 'DropdownListGroup',
	components: {
		Search
	},
	props: {
		options: {
			type: Array,
			default: () => [],
			validator: value =>
				Array.isArray(value) &&
				value.every(option => 'text' in option && 'value' in option)
		},
		value: {
			type: Array,
			default: () => []
		},
		timeout: {
			type: Number,
			default: 1000
		},
		searchPlaceholder: {
			type: String
		}
	},
	data() {
		return {
			uniqueId: Date.now(),
			optionsLocal: [],
			searchQuery: '',
			selectedValues: [...this.value],
			selectedValuesCopy: [...this.value]
		};
	},
	created() {
		this.optionsLocal = _.cloneDeep(this.options);
	},
	computed: {
		hasSelectedItemsAndLessThanTotalOptions() {
			return (
				this.selectedValues.length > 0 &&
				this.selectedValues.length < this.optionsLocal.length
			);
		}
	},
	methods: {
		previousItem() {
			// to implement
		},
		nextItem() {
			// to implement
		},
		selectItem() {
			this.$emit('input', this.selectedValues);
			this.$emit('change', this.selectedValues);
		},
		deselectItem() {
			this.selectedValues = [...this.selectedValuesCopy];
			this.selectItem();
		}
	},
	watch: {
		value(newValue) {
			this.selectedValues = [...newValue];
			this.selectedValuesCopy = [...newValue];
		},
		options(newValue) {
			this.optionsLocal = _.cloneDeep(newValue);
		},
		searchQuery(newValue) {
			if (newValue) {
				const valueToSearch = decodeURI(newValue.toLowerCase());

				this.optionsLocal = this.options.filter(option =>
					option.text?.toLowerCase().includes(valueToSearch)
				);
			} else {
				this.optionsLocal = [...this.options];
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@/scss/5-objects/_form.scss';

.checkbox {
	@include create-check-element('.checkbox__input', 15px, 15px, 2px);
	position: relative;

	input.checkbox__input {
		position: absolute;
		margin-left: 8px !important; // UI/UX please update

		&:checked {
			&::after {
				@include vertical-horizontal-center-with-transform;
				font-family: 'vue-support-icons';
				font-size: 9px;
				height: 9px;
				color: $accent-text-dark;
				content: '\e92e';
			}
		}
	}

	label {
		cursor: pointer;
		padding: $padding-sm;
		padding-left: 32px;
		flex-grow: 2;
	}
}

.dropdown-list-multiselect {
	width: max-content;
	min-width: 100%;
	@include stylized-dropdown;
	background: $white;
	display: inline-block;
	padding: $padding-sm;
	z-index: 999;

	&__items {
		padding: $padding-sm 0;
		margin-block-start: 0;
		margin-block-end: 0;
	}

	.dropdown-list {
		padding-inline-start: 0;
		margin-block-start: 2px;
		margin-block-end: 0;
		font-size: $default-font-size - 2;
		max-height: 200px; // display scroll if there's more than 10 items
		overflow-y: auto;
		@include scrollbar;
		padding: $padding-sm 0;

		&__item {
			&:active,
			&:focus {
				::v-deep .checkbox__input {
					border: $form-input-border;
					background-color: $form-input-bkgd;

					&::before {
						content: '';
						@include vertical-horizontal-center-with-transform;
						background-color: none;
						width: 24px;
						height: 24px;
						border: $form-input-border--focus;
						border-radius: $border-radius;
					}
				}
			}

			&:checked {
				border: $form-input-border--checked;
			}

			&:focus-visible {
				outline: none;
			}

			&.is-active {
				background: $neutral-4;
				@include text-contrast($neutral-4, $white, $black);
			}

			&:hover,
			&.selected {
				background: lighten($neutral-4, 5%);
				@include text-contrast(lighten($neutral-4, 5%), $white, $black);
			}

			&.disabled {
				pointer-events: none;
				opacity: $disabled-opacity;
			}

			.list-item {
				display: inline-flex;
				width: 100%;

				&__link {
					word-break: break-all;
				}
			}
		}

		&__item--div {
			border: 1px solid $neutral-4;
			margin-right: $gutter-sm;
		}
	}
}
</style>
